import React, { useCallback, useEffect, useRef } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const DetailDialog = ({
  open,
  title,
  onClose,
  children,
  footer,
  variant = "sheet",
  labelledBy,
  className = "",
}) => {
  const panelRef = useRef(null);
  const restoreRef = useRef(null);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();

        return;
      }

      if (event.key !== "Tab") return;

      const panel = panelRef.current;
      if (!panel) return;

      const targets = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
        (node) => node.offsetParent !== null
      );

      if (!targets.length) return;

      const first = targets[0];
      const last = targets[targets.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return undefined;

    restoreRef.current = document.activeElement;

    const panel = panelRef.current;
    const target = panel?.querySelector(FOCUSABLE) || panel;

    target?.focus?.();

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = overflow;
      restoreRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className={`sy-dialog-root sy-dialog-${variant}`}>
      <button
        type="button"
        className="sy-dialog-backdrop"
        aria-label="Close"
        onClick={onClose}
      />

      <div
        ref={panelRef}
        className={`sy-dialog-panel ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={labelledBy ? undefined : title}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header className="sy-dialog-header">
          <h2 className="sy-dialog-title">{title}</h2>

          <button
            type="button"
            className="sy-dialog-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
        </header>

        <div className="sy-dialog-body">{children}</div>

        {footer && <footer className="sy-dialog-footer">{footer}</footer>}
      </div>
    </div>
  );
};

export default DetailDialog;
